import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { router } from './components/AppRoutes';
import { appTheme } from './appTheme/appTheme';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { storeBuilder } from './storeBuilder';
import { ldProviderBuilder } from './ldProviderBuilder';
import { SweepDesignProvider } from '@sweep-io/sweep-design';
import { initMonitoring } from './services/monitoring';
import { initBi } from './services/mixpanel';
import { initIntercom } from './services/intercom';
import { initHubspot } from './services/hubspot';
import { initReddit } from './services/reddit';
import { CssBaseline } from '@mui/material';
import { RouterProvider } from 'react-router';
import { ConfirmContextProvider } from './components/common/dialogs/ConfirmLeaveWithoutSave/ConfirmContext';
import { UnsavedPromptProvider } from './components/pages/funnel-map-flow/prompt/UnsavedPromptContext';
import { telemetry } from './telemetry';

const disableStrictMode = Boolean(import.meta.env.VITE_DISABLE_STRICT_MODE);

const MaybeStrictMode = disableStrictMode ? React.Fragment : React.StrictMode;

const captureReactRootError = (
  source: 'uncaught' | 'caught' | 'recoverable',
  error: unknown,
  errorInfo: {
    componentStack?: string | undefined;
    errorBoundary?: React.Component<unknown> | undefined;
  },
  level: 'error' | 'warning' = 'error',
) => {
  const boundary = errorInfo.errorBoundary;
  telemetry.captureError(
    error,
    {
      'react.errorSource': source,
      componentStack: errorInfo.componentStack,
      ...(boundary !== undefined && {
        errorBoundary: (boundary as any).displayName ?? (boundary as any).constructor?.name,
      }),
    },
    level,
  );
};

(async () => {
  initMonitoring();
  initBi();
  initIntercom();
  initHubspot();
  initReddit();

  const container = document.getElementById('root');
  const root = createRoot(container!, {
    onUncaughtError(error, errorInfo) {
      captureReactRootError('uncaught', error, errorInfo);
    },
    onCaughtError(error, errorInfo) {
      captureReactRootError('caught', error, errorInfo);
    },
    onRecoverableError(error, errorInfo) {
      captureReactRootError('recoverable', error, errorInfo, 'warning');
    },
  });
  const { store } = storeBuilder();
  const LDProvider = await ldProviderBuilder();

  root.render(
    <MaybeStrictMode>
      <CssBaseline />
      <LDProvider>
        <SweepDesignProvider legacyTheme={appTheme}>
          <ConfirmContextProvider>
            <UnsavedPromptProvider>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <Provider store={store}>
                  <RouterProvider router={router} />
                </Provider>
              </LocalizationProvider>
            </UnsavedPromptProvider>
          </ConfirmContextProvider>
        </SweepDesignProvider>
      </LDProvider>
    </MaybeStrictMode>,
  );
})();
